import React, { useState, useEffect, useCallback } from 'react';
import { GoogleMap, useLoadScript, Marker, InfoWindow } from '@react-google-maps/api';
import { Box, Typography, Button, Chip, Avatar, Paper, CircularProgress } from '@mui/material';
import { LocationOn, Directions, Info } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import API_BASE_URL from '../apiConfig';

const ClubMapView = ({ clubs, userLocation, onLocationRequest }) => {
  const [selectedClub, setSelectedClub] = useState(null);
  const [mapCenter, setMapCenter] = useState({ lat: 32.7157, lng: -117.1611 }); // San Diego default
  const [mapZoom, setMapZoom] = useState(10);

  // Load Google Maps script
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '',
    libraries: ['places']
  });

  // Update map center when user location changes
  useEffect(() => {
    if (userLocation) {
      setMapCenter(userLocation);
      setMapZoom(12);
    }
  }, [userLocation]);

  const handleMarkerClick = (club) => {
    setSelectedClub(club);
  };

  const handleCloseInfoWindow = () => {
    setSelectedClub(null);
  };

  const getDirections = (club) => {
    if (club.maps_url) {
      window.open(club.maps_url, '_blank');
    }
  };

  const extractCoordinates = (mapsUrl) => {
    try {
      const url = new URL(mapsUrl);
      
      // Handle different Google Maps URL formats
      let lat, lng;
      
      // Format 1: ?lat=32.7157&lng=-117.1611
      if (url.searchParams.has('lat') && url.searchParams.has('lng')) {
        lat = parseFloat(url.searchParams.get('lat'));
        lng = parseFloat(url.searchParams.get('lng'));
      }
      // Format 2: ?q=32.7157,-117.1611
      else if (url.searchParams.has('q')) {
        const coords = url.searchParams.get('q').split(',');
        if (coords.length === 2) {
          lat = parseFloat(coords[0]);
          lng = parseFloat(coords[1]);
        }
      }
      // Format 3: @32.7157,-117.1611,15z (embedded coordinates)
      else if (url.pathname.includes('@')) {
        const coordMatch = url.pathname.match(/@(-?\d+\.?\d*),(-?\d+\.?\d*)/);
        if (coordMatch) {
          lat = parseFloat(coordMatch[1]);
          lng = parseFloat(coordMatch[2]);
        }
      }
      // Format 4: Shortened URLs (maps.app.goo.gl/...)
      else if (url.hostname === 'maps.app.goo.gl' || url.hostname === 'goo.gl') {
        // For shortened URLs, we can't extract coordinates directly
        // These would need to be handled with the Geocoding API or URL expansion
        console.warn('Shortened Google Maps URL detected. Coordinates cannot be extracted directly:', mapsUrl);
        return null;
      }
      
      if (lat && lng && !isNaN(lat) && !isNaN(lng)) {
        return { lat, lng };
      }
      
      return null;
    } catch (e) {
      console.warn('Could not parse maps URL:', mapsUrl, e);
      return null;
    }
  };

  const isShortenedUrl = (mapsUrl) => {
    try {
      const url = new URL(mapsUrl);
      return url.hostname === 'maps.app.goo.gl' || url.hostname === 'goo.gl';
    } catch (e) {
      return false;
    }
  };

  const getUrlType = (mapsUrl) => {
    try {
      const url = new URL(mapsUrl);
      
      if (url.searchParams.has('lat') && url.searchParams.has('lng')) {
        return 'coordinates';
      } else if (url.searchParams.has('q')) {
        return 'query';
      } else if (url.pathname.includes('@')) {
        return 'embedded';
      } else if (url.hostname === 'maps.app.goo.gl' || url.hostname === 'goo.gl') {
        return 'shortened';
      } else {
        return 'unknown';
      }
    } catch (e) {
      return 'invalid';
    }
  };

  const mapContainerStyle = {
    width: '100%',
    height: '600px',
    borderRadius: '12px',
    overflow: 'hidden'
  };

  const defaultCenter = {
    lat: 32.7157,
    lng: -117.1611
  };

  const onMapLoad = useCallback((map) => {
    // Fit bounds to include all clubs if no user location
    if (!userLocation && clubs.length > 0) {
      const bounds = new window.google.maps.LatLngBounds();
      clubs.forEach(club => {
        if (club.maps_url) {
          const coords = extractCoordinates(club.maps_url);
          if (coords) {
            bounds.extend(coords);
          }
        }
      });
      if (!bounds.isEmpty()) {
        map.fitBounds(bounds);
      }
    }
  }, [clubs, userLocation, extractCoordinates]);

  // Show loading state
  if (!isLoaded) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Show error state
  if (loadError) {
    return (
      <Box sx={{ textAlign: 'center', p: 4 }}>
        <Typography variant="h6" color="error" gutterBottom>
          Error loading Google Maps
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Please check your internet connection and try again.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      {/* Location Permission Section */}
      {!userLocation && (
        <Paper sx={{ p: 3, mb: 3, textAlign: 'center', bgcolor: '#f8f9fa' }}>
          <Typography variant="h6" gutterBottom sx={{ color: '#e91e63' }}>
            Get Personalized Club Recommendations
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Allow location access to see clubs near you and get distance information
          </Typography>
          <Button
            variant="contained"
            startIcon={<LocationOn />}
            onClick={onLocationRequest}
            sx={{ 
              bgcolor: '#e91e63', 
              color: '#ffffff',
              '&:hover': { 
                bgcolor: '#c2185b',
                color: '#ffffff'
              } 
            }}
          >
            Share My Location
          </Button>
          
          {/* URL Format Information */}
          <Box sx={{ mt: 2, p: 2, bgcolor: 'rgba(0,0,0,0.05)', borderRadius: 1 }}>
            <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
              <strong>Note:</strong> Only clubs with coordinate-based URLs are displayed on the map
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block">
              Supported: ?lat=X&lng=Y, ?q=X,Y, @X,Y,Z • Not supported: Shortened URLs (maps.app.goo.gl)
            </Typography>
          </Box>
        </Paper>
      )}

      {/* Map Container */}
      <GoogleMap
        mapContainerStyle={mapContainerStyle}
        center={mapCenter}
        zoom={mapZoom}
        onLoad={onMapLoad}
        options={{
          styles: [],
          disableDefaultUI: false,
          zoomControl: true,
          streetViewControl: false,
          mapTypeControl: true,
          fullscreenControl: true
        }}
      >
        {/* User Location Marker */}
        {userLocation && (
          <Marker
            position={userLocation}
            icon={{
              url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="12" r="8" fill="#4285f4" stroke="white" stroke-width="2"/>
                  <circle cx="12" cy="12" r="3" fill="white"/>
                </svg>
              `),
              scaledSize: new window.google.maps.Size(24, 24)
            }}
            title="Your Location"
          />
        )}

        {/* Club Markers */}
        {clubs.map((club) => {
          const coords = extractCoordinates(club.maps_url);
          if (!coords) return null;

          return (
            <Marker
              key={club.id}
              position={coords}
              onClick={() => handleMarkerClick(club)}
              icon={{
                url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
                  <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="16" cy="16" r="14" fill="#e91e63" stroke="white" stroke-width="2"/>
                    <text x="16" y="20" text-anchor="middle" fill="white" font-family="Arial" font-size="12" font-weight="bold">G</text>
                  </svg>
                `),
                scaledSize: new window.google.maps.Size(32, 32)
              }}
              title={club.name}
            />
          );
        })}



        {/* Info Window */}
        {selectedClub && (
          <InfoWindow
            position={extractCoordinates(selectedClub.maps_url)}
            onCloseClick={handleCloseInfoWindow}
          >
            <Box sx={{ p: 1, minWidth: 250 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                {selectedClub.avatar_url && (
                  <Avatar 
                    src={selectedClub.avatar_url} 
                    sx={{ width: 40, height: 40, mr: 2 }}
                    alt={selectedClub.name}
                  />
                )}
                <Box>
                  <Typography variant="h6" fontWeight="bold" sx={{ color: '#e91e63' }}>
                    {selectedClub.name}
                  </Typography>
                  {selectedClub.category_name && (
                    <Chip 
                      label={selectedClub.category_name} 
                      size="small" 
                      sx={{ bgcolor: '#f0f0f0', color: '#666' }}
                    />
                  )}
                </Box>
              </Box>
              
              {selectedClub.description && (
                <Typography variant="body2" sx={{ mb: 2, color: '#333333' }}>
                  {selectedClub.description}
                </Typography>
              )}

              {selectedClub.distance_formatted && (
                <Typography variant="body2" sx={{ mb: 2, fontWeight: 500, color: '#000000' }}>
                  📍 {selectedClub.distance_formatted} away
                </Typography>
              )}

              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button
                  component={Link}
                  to={`/clubs/${selectedClub.handle}`}
                  variant="outlined"
                  size="small"
                  startIcon={<Info />}
                  sx={{ borderColor: '#e91e63', color: '#e91e63' }}
                >
                  View Club
                </Button>
                {selectedClub.maps_url && (
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<Directions />}
                    onClick={() => getDirections(selectedClub)}
                    sx={{ 
                      bgcolor: '#e91e63', 
                      color: '#ffffff',
                      '&:hover': { bgcolor: '#c2185b' } 
                    }}
                  >
                    Directions
                  </Button>
                )}
              </Box>
            </Box>
          </InfoWindow>
        )}
      </GoogleMap>
    </Box>
  );
};

export default ClubMapView;
